import { useState } from "react";
import { X, LifeBuoy, Search, ChevronRight, MousePointerClick, PlayCircle } from "lucide-react";
import { SECTION_TOUR } from "@/lib/tours";
import { useI18n } from "@/context/I18nContext";

// [tytuł, opis, target?(data-testid do „Pokaż mnie")]
const SECTIONS = [
  {
    id: "start",
    title: "1. Pierwsze kroki",
    items: [
      ["Logowanie i role", "Wpisz e-mail i hasło, kliknij „Zaloguj się”. Role: ADMINISTRATOR (pełny dostęp, ustawienia, użytkownicy), PROJEKTANT (dodawanie/edycja/usuwanie danych sieci), ODCZYT (tylko podgląd, bez edycji)."],
      ["Układ ekranu", "Po lewej: pasek modułów i narzędzi. W centrum: mapa. Po prawej u góry: panel warstw i pasek statystyk. Panele modułów wysuwają się z lewej, a Dokumentacja/Katalogi/Pomoc z prawej."],
      ["Moduł Pomoc", "Tutaj jesteś. Znajdziesz opis każdej funkcji z instrukcją krok po kroku. Użyj pola „Szukaj w pomocy”, aby błyskawicznie odnaleźć temat.", "sidebar-help"],
      ["Panel warstw", "Ikona warstw (prawy górny róg mapy) — włączaj/wyłączaj widoczność modułów i bazy adresowej. Warstwa Zasięgi ma dodatkowo filtr i legendę.", "layers-toggle"],
      ["Pasek statystyk", "Najedź na prawą krawędź mapy — pokaże się panel z licznikami sieci oraz pokryciem zasięgów wg medium i technologii.", "stats-drawer-trigger"],
    ],
  },
  {
    id: "map",
    title: "2. Praca z mapą",
    items: [
      ["Dodanie obiektu punktowego", "Otwórz moduł punktowy (Słupy, Mufy, Splittery, OLT, Klienci, Zasięgi). Kliknij „+ Dodaj” i wpisz dane, albo kliknij ikonę pinezki „Dodaj na mapie” i wskaż punkt na mapie — współrzędne GPS uzupełnią się same. Zapisz przyciskiem na dole formularza."],
      ["Rysowanie trasy lub kabla", "W module liniowym (Trasy, Kable) kliknij ikonę rysowania, następnie klikaj kolejne wierzchołki na mapie. Linia „przyskakuje” (snap) do pobliskich słupów i muf. Podwójne kliknięcie lub przycisk na banerze kończy rysowanie."],
      ["Edycja obiektu", "Kliknij pozycję na liście modułu albo marker na mapie → otworzy się karta szczegółów. Ikony przy pozycji: 🔍 lokalizuj na mapie, ✏️ edytuj, 🗑️ usuń (tylko admin/projektant)."],
      ["Filtrowanie i szukanie", "W panelu modułu użyj pola „Filtruj…”. Na górze mapy jest wyszukiwarka globalna (klient, kabel, mufa) oraz pole „Adres…” przeszukujące bazę adresową."],
      ["Klasteryzacja", "Przy dużej liczbie obiektów mapa grupuje je w klastry z liczbą. Przybliżaj (scroll / +), aby je rozwinąć."],
    ],
  },
  {
    id: "infra",
    title: "3. Infrastruktura pasywna",
    items: [
      ["Słupy", "Dodaj słup: numer, nr ewidencyjny, właściciel, typ (np. żelbetowy/drewniany), materiał, wysokość, stan techniczny. Słupy są punktami podparcia tras i lokalizacji muf.", "sidebar-poles"],
      ["Trasy kablowe", "Definiują drogę kabla: typ (napowietrzna / kanalizacja / mikrokanalizacja / doziemna HDPE), długość, liczba i zajętość mikrorurek. Kable prowadzisz wzdłuż tras."],
      ["Kable", "Podaj numer, producenta, model z katalogu (auto-parametry), liczbę włókien, mufy końcowe A i B, tłumienie 1310/1550 nm, długość, status. Przycisk „Włókna” otwiera edytor: zielone = wolne, czerwone = zajęte/uszkodzone, pomarańczowe = przecięte.", "sidebar-cables"],
      ["Mufy", "Dodaj mufę: typ (kopułowa/przelotowa/leżakowa/naścienna), liczba tacek, pojemność spawów. W widoku mufy zobaczysz kable dochodzące (A/B), przelotowe oraz wykryte geometrycznie na trasie (≤3 m). Stąd wykonasz spawy i cięcia włókien.", "sidebar-closures"],
    ],
  },
  {
    id: "active",
    title: "4. Elementy aktywne i logiczne",
    items: [
      ["Splittery", "Wybierz typ podziału (1:2 … 1:64) i przypisz do mufy oraz kabla/włókna wejściowego. KASKADY: wskazując splitter nadrzędny, system sam uzupełnia kabel, kolor i włókno oraz tworzy spawy w obu mufach.", "sidebar-splitters"],
      ["Spawy", "Ewidencja splotów na tackach: kabel/włókno wejściowe → wyjściowe (lub do splittera), kolor włókna, tłumienie spawu, monter, data. Najłatwiej dodawać spawy bezpośrednio w widoku mufy."],
      ["ODF / OLT / karty OLT / ONU", "Odwzoruj ścieżkę aktywną: przełącznica ODF, urządzenie OLT, karty liniowe i porty PON, terminale ONU u abonentów. Dzięki temu trasowanie zna pełną drogę sygnału."],
      ["Klienci", "Dodaj abonenta i przypnij do splittera/kabla/włókna. Status mocy (zielony OK / pomarańczowy ostrzeżenie / czerwony krytyczny) wyliczany jest z bilansu optycznego wg progów z Ustawień.", "sidebar-clients"],
      ["Kable klienckie i Monterzy", "Kable klienckie to przyłącza abonenckie od splittera/mufy do klienta. Monterzy to słownik wykonawców używany przy rejestrowaniu spawów."],
    ],
  },
  {
    id: "coverage",
    title: "5. Zasięgi Sieci (UKE/PIT)",
    items: [
      ["Otwórz moduł Zasięgi", "Ewidencja pokrycia usługami zgodna z logiką inwentaryzacji UKE.", "sidebar-coverage"],
      ["Dodanie zasięgu", "Kliknij „Dodaj na mapie” i wskaż budynek, ALBO kliknij kropkę w bazie adresowej → „Dodaj zasięg”. Pola adresowe (miejscowość, gmina, powiat, kod SIMC, kod ULIC, nr budynku) uzupełnią się automatycznie z bazy adresowej."],
      ["Identyfikator (auto)", "Tworzy się automatycznie: kod SIMC + kod ULIC + numer budynku. Pole jest tylko do odczytu."],
      ["Parametry usługi", "Uzupełnij: medium transmisyjne, technologię dostępową, przedstawiciela, rodzaj usługi (hurtowa/detaliczna — wielokrotny wybór), infrastrukturę własną (tak/nie), maks. przepustowość DL i UL oraz rodzaj zasięgu (rzeczywisty/teoretyczny)."],
      ["Filtr i legenda na mapie", "W panelu warstw pod „Zasięgi Sieci”: Wszystkie / Tylko rzeczywiste (zielone) / Tylko teoretyczne (bursztynowe).", "layers-toggle"],
      ["Eksport UKE (CSV/XLSX)", "W panelu modułu wybierz format i zakres (wszystkie / rzeczywiste / teoretyczne) i kliknij Eksport. Kody przepustowości są zgodne ze słownikiem UKE (2 Mb/s=01 … 10000 Mb/s=29)."],
      ["Import UKE", "Kliknij „Import UKE” i wskaż plik CSV/XLSX. System rozpozna nagłówki kolumn i doda rekordy. Wskazówka: kolumny gmina/powiat/ULIC muszą być w pliku, by uzupełniały się automatycznie."],
      ["Słowniki zasięgów", "Medium, technologie i przepustowości edytujesz w Ustawieniach → „Zasięgi sieci — słowniki”.", "sidebar-settings"],
    ],
  },
  {
    id: "trace",
    title: "6. Trasowanie sygnału i bilans optyczny",
    items: [
      ["Śledzenie trasy", "W szczegółach klienta uruchom trasę. System pokaże ścieżkę OLT → splittery (cała kaskada) → klient na mapie oraz w schemacie logicznym, licząc łączną liczbę spawów i splitterów."],
      ["Bilans mocy", "Program sumuje tłumienie: włókna (dł. × tłumienie/km), spawy oraz splittery, dla wybranej długości fali. Wartość „Straty łącza” jest identyczna z „Tłumienie” w trasie."],
      ["Progi i statusy", "W Ustawieniach → Budżet optyczny ustaw moc TX OLT, minimalne RX oraz tolerancję. Klienci otrzymują status OK / ostrzeżenie / krytyczny (kolor markera na mapie).", "sidebar-settings"],
    ],
  },
  {
    id: "closurework",
    title: "7. Mufy, spawy i splittery — praca szczegółowa",
    items: [
      ["Widok mufy", "Otwórz mufę → sekcja „Kable w mufie” pokazuje kable końcowe (A/B), przelotowe i wykryte na trasie. Przy każdym kablu przycisk „Włókna” otwiera edytor włókien tego kabla."],
      ["Spaw i przelotka", "W widoku mufy łącz włókna dwóch kabli (spaw) lub prowadź włókno przelotowo. W mufach przelotowych możesz też przeciąć włókno w danym punkcie."],
      ["Kaskada splitterów", "Dodając splitter „dziecko” wskaż splitter nadrzędny — kabel/kolor/włókno uzupełnią się automatycznie, a spawy powstaną w obu mufach. Trasa uwzględni całą kaskadę."],
    ],
  },
  {
    id: "tools",
    title: "8. Narzędzia",
    items: [
      ["Eksport danych", "Menu „Eksport” — pobierz dane modułów jako CSV (paczka ZIP) lub KML (Google Earth).", "sidebar-export"],
      ["Połączenia", "Podgląd i zarządzanie połączeniami elementów sieci.", "sidebar-connections"],
      ["Etykiety QR", "Generuj i drukuj etykiety QR do oznaczania elementów w terenie.", "sidebar-qrlabels"],
      ["Asystent AI", "Wykrywanie błędów w dokumentacji i propozycje tras przyłączeń. Własny klucz API ustawisz w Ustawieniach (patrz sekcja 10).", "sidebar-ai"],
      ["Raporty i Historia", "W Ustawieniach: „Raporty” (PDF/CSV/XLSX, m.in. metryka łącza) oraz „Historia” (dziennik operacji: kto, co, kiedy).", "sidebar-settings"],
      ["Opis programu (PDF)", "Ustawienia → „Pobierz opis programu (PDF)” — automatycznie generowany, aktualny opis wszystkich funkcji, ze stroną tytułową i Twoimi danymi firmy.", "sidebar-settings"],
    ],
  },
  {
    id: "addresses",
    title: "9. Baza adresowa",
    items: [
      ["Import adresów", "Ustawienia → „Baza adresowa” — wgraj CSV per województwo. Aby działało auto-uzupełnianie gminy/powiatu/kodu ULIC, plik musi zawierać te kolumny (oraz szerokość/długość, SIMC).", "sidebar-settings"],
      ["Użycie na mapie", "Włącz warstwę bazy adresowej. Kliknij punkt adresu, aby błyskawicznie dodać KLIENTA lub ZASIĘG z gotowymi danymi adresowymi.", "layers-toggle"],
    ],
  },
  {
    id: "settings",
    title: "10. Ustawienia, firma i AI",
    items: [
      ["Otwórz Ustawienia", "Wszystkie konfiguracje i narzędzia administracyjne.", "sidebar-settings"],
      ["Zmiana hasła", "Ustawienia → „Zmiana własnego hasła”. Dostępne dla każdego użytkownika."],
      ["Dane firmy (do PDF)", "Ustawienia → „Dane firmy”: nazwa, NIP, telefon, e-mail. Trafiają automatycznie na stronę tytułową i do stopki pobieranego opisu programu (PDF)."],
      ["Własny klucz AI", "Ustawienia → „Asystent AI — własny klucz”: wybierz dostawcę (OpenAI), model (np. gpt-4o-mini) i wklej swój klucz API. Wtedy AI działa na Twoim koncie. Puste pole klucza = bez zmian. Klucz jest maskowany i nigdy nie jest zwracany z serwera."],
      ["Słowniki i budżet (admin)", "Technologie klientów, słowniki zasięgów, tłumienia splitterów i budżet optyczny — konfiguruje administrator."],
      ["Użytkownicy (admin)", "Administrator dodaje konta i przypisuje role (administrator/projektant/odczyt)."],
    ],
  },
  {
    id: "faq",
    title: "11. Najczęstsze pytania (FAQ)",
    items: [
      ["Nie widzę obiektów na mapie", "Sprawdź panel warstw — dana warstwa może być wyłączona. Upewnij się też, że obiekt ma współrzędne (lat/lng)."],
      ["Kabel nie rysuje się do mufy", "Rysuj kabel tak, aby koniec znalazł się blisko mufy (system łączy automatycznie w promieniu ~3 m) lub ustaw mufę końcową A/B w formularzu kabla."],
      ["„Straty łącza” różnią się od „Tłumienia”", "Obie wartości są ujednolicone — jeśli widzisz różnicę, odśwież trasę. Sprawdź tłumienia włókien i tłumienia splitterów w Ustawieniach."],
      ["Gmina/Powiat/ULIC nie uzupełniają się z adresu", "Zaimportuj ponownie bazę adresową plikiem zawierającym kolumny gmina, powiat i ULIC."],
      ["Jak zmienić dane na stronie tytułowej PDF?", "Ustawienia → Dane firmy. Po zapisaniu pobierz PDF ponownie — dane pojawią się automatycznie."],
      ["Czy AI używa mojego klucza?", "Tak — po wpisaniu klucza w Ustawieniach AI korzysta z Twojego konta OpenAI. Bez klucza używany jest mechanizm domyślny."],
    ],
  },
  {
    id: "trouble",
    title: "12. Rozwiązywanie problemów",
    items: [
      ["Mapa jest szara / kafle się nie ładują", "Sprawdź połączenie z internetem (kafle OpenStreetMap pobierane są online) i odśwież stronę."],
      ["Nie mogę edytować / usuwać", "Twoja rola to zapewne „odczyt”. Poproś administratora o rolę projektant lub administrator."],
      ["Zmiany się nie zapisują", "Sprawdź, czy pojawił się komunikat błędu (prawy dolny róg). Uzupełnij pola wymagane oznaczone gwiazdką."],
      ["AI zwraca błąd po ustawieniu klucza", "Zweryfikuj poprawność klucza API i nazwę modelu. Aby wrócić do trybu domyślnego, wyczyść pole klucza (skontaktuj się z administratorem bazy)."],
    ],
  },
];

export default function HelpPanel({ onClose, onShowMe, onStartTour }) {
  const { t: tr } = useI18n();
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const sections = SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter(([t, d]) => !ql
      || t.toLowerCase().includes(ql) || d.toLowerCase().includes(ql)
      || tr(t).toLowerCase().includes(ql) || tr(d).toLowerCase().includes(ql)),
  })).filter((s) => s.items.length > 0);

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="help-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
        <div className="flex items-center gap-2"><LifeBuoy size={18} className="text-green-400" /><h2 className="font-heading font-bold text-lg">{tr("Pomoc — pełna instrukcja obsługi")}</h2></div>
        <button onClick={onClose} data-testid="help-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
      </div>

      <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Szukaj w pomocy…")} data-testid="help-search"
            className="w-full bg-slate-800 border border-slate-700 rounded pl-8 pr-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-green-500" />
        </div>
        <span className="text-[11px] text-slate-500 flex items-center gap-1"><MousePointerClick size={13} className="text-cyan-400" /> {tr("„Pokaż mnie” podświetli element na ekranie.")}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-6 max-w-4xl">
        <p className="text-slate-400 text-sm">
          {tr("Szczegółowa instrukcja obsługi programu Piotr Niemiec Fiber 1. Każda funkcja ma opis „jak użyć”, a wiele pozycji przycisk „Pokaż mnie”, który zamknie pomoc i podświetli właściwy element interfejsu. Skorzystaj z wyszukiwarki powyżej, aby szybko odnaleźć temat.")}
        </p>
        {sections.map((s) => (
          <section key={s.id} data-testid={`help-section-${s.id}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-bold text-base text-green-400 flex items-center gap-2">
                <ChevronRight size={16} /> {tr(s.title)}
              </h3>
              {SECTION_TOUR[s.id] && (
                <button onClick={() => onStartTour && onStartTour(SECTION_TOUR[s.id])} data-testid={`help-tour-${s.id}`}
                  className="flex items-center gap-1 text-[11px] text-cyan-200 border border-cyan-700 bg-cyan-900/20 hover:bg-cyan-900/40 rounded px-2 py-1">
                  <PlayCircle size={13} /> {tr("Przewodnik")}
                </button>
              )}
            </div>
            <div className="space-y-2">
              {s.items.map(([t, d, target], i) => (
                <div key={i} className="bg-slate-800/60 border border-slate-800 rounded-md px-3 py-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm font-semibold text-slate-100">{tr(t)}</div>
                    {target && (
                      <button onClick={() => onShowMe && onShowMe(target)} data-testid={`help-show-${target}`}
                        className="shrink-0 flex items-center gap-1 text-[11px] text-cyan-300 border border-cyan-800 bg-cyan-900/20 hover:bg-cyan-900/40 rounded px-2 py-1">
                        <MousePointerClick size={12} /> {tr("Pokaż mnie")}
                      </button>
                    )}
                  </div>
                  <div className="text-sm text-slate-300 leading-relaxed mt-0.5">{tr(d)}</div>
                </div>
              ))}
            </div>
          </section>
        ))}
        {sections.length === 0 && <div className="text-slate-500 text-sm">{tr("Brak wyników dla")} „{q}”.</div>}
      </div>
    </div>
  );
}
